import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import dayjs from 'dayjs';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {usePrint} from '../../../hooks/print';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import usePrevious from '../../../hooks/usePrevious';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  GasMonitoringNodeListQuery,
  GasMonitoringNodeListResponse,
} from '../../../interfaces/GasMonitoringNode';
import {
  PRINT_HIDDEN_SX,
  PRINTABLE_CONTAINER_SX,
  PRINTABLE_MAP_SX,
} from '../../../utils/printStyles';
import AMSLocationHistoryExportButton from '../../ams-sensor/AMSLocationHistoryExportButton';
import {AdaptiveButtonGroup} from '../../common/AdaptiveButtonGroup';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {Map, MapLayerId} from '../../common/Map';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  activeId?: string;
  openedItems: string[];
  tabData?: Record<string, AMSLocationHistoryReportDataTab>;
  mapLayers: MapLayerId[];
  mapLevel: number | null;
  onUpdate?: (updates: {
    activeId?: string;
    openedItems?: string[];
    tabData?: Record<string, AMSLocationHistoryReportDataTab>;
    mapLayers?: MapLayerId[];
    mapLevel?: number | null;
  }) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'commtrac_external_id',
  'pos_lat',
  'pos_lon',
  'date',
  'beacon_data',
];

interface AMSLocationHistoryReportDataTab {
  id: string;
  refreshInterval?: number | null;
  selectedIds?: string[];
  selectAll?: boolean;
  params?: {
    date_start?: string;
    date_end?: string;
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
    status?: 'active' | 'inactive' | 'unacknowledged' | 'all';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export interface AMSLocationHistoryReportData {
  mapLayers: MapLayerId[];
  mapLevel: number | null;
  tabs: Record<string, AMSLocationHistoryReportDataTab>;
}

export const getAMSLocationHistoryReportData =
  (): AMSLocationHistoryReportData => ({
    tabs: {},
    mapLayers: ['mine', 'ams_location'],
    mapLevel: null,
  });

export const getAMSLocationHistoryReportDataTab = (
  id: string
): AMSLocationHistoryReportDataTab => ({
  id,
  selectAll: true,
  params: {
    date_start: dayjs().format('YYYY-MM-DD'),
    date_end: dayjs().format('YYYY-MM-DD'),
  },
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

export const AMSLocationHistoryReport = ({
  activeId,
  openedItems,
  tabData,
  mapLayers,
  mapLevel,
  onUpdate,
}: Props) => {
  const isOpenAwayFromConnectView = useMemo(() => {
    return (
      location.pathname.includes('/panels/') ||
      !document.getElementById('connect-view-panel')
    );
  }, [location, activeId]);

  const sensorId = useMemo(() => {
    return activeId?.split('-')?.[1];
  }, [activeId]);

  const openedItem = useMemo(
    () =>
      activeId && tabData?.[activeId]
        ? tabData[activeId]
        : activeId
          ? getAMSLocationHistoryReportDataTab(activeId)
          : null,
    [activeId, tabData]
  );

  //const amsNodes = useAppSelector(({assets}) => assets.ams_nodes);
  const amsNodes = useAppSelector(({assets}) => {
    const seen = new Set<number>();
    return assets.ams_nodes
      .filter((el) => el.ack === '1')
      .filter((el) => {
        if (seen.has(el.commtrac_external_id)) {
          return false;
        }
        seen.add(el.commtrac_external_id);
        return true;
      });
  });
  const tabNames = useMemo(
    () =>
      openedItems.map((id) => {
        const sensor_id = +id.split('-')?.[1];
        return amsNodes.find((i) => Number(i.id) === Number(sensor_id))?.name;
      }),
    [openedItems, amsNodes]
  );

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<GasMonitoringNodeListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<GasMonitoringNodeListQuery | null>(
    () =>
      openedItem?.id
        ? {
            date_start:
              openedItem.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
            date_end:
              openedItem.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? 'date',
            dir: openedItem.params?.dir ?? 'DESC',
            status: openedItem.params?.status ?? 'all',
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );

  const fetchData = useCallback(
    async (params: GasMonitoringNodeListQuery) => {
      if (!sensorId) {
        return;
      }
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/ams/${sensorId}/location-history`;
        const resp = await API.get<GasMonitoringNodeListResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_7807b09c');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [sensorId]
  );

  useEffect(() => {
    if (params && sensorId) {
      fetchData(params);
    }
  }, [params, sensorId, fetchData]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/

  const callFetchData = useCallback(() => {
    params && fetchData(params);
  }, [params]);

  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*********/
  /* grid */
  /*********/
  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );
  const dataGridRef = useRef<DataGridRef>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [printReport] = usePrint(printContainerRef.current);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<any>[] = [
    {
      field: 'select',
      type: 'select',
      width: 60,
      hideable: false,
      doNotExport: true,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(`${row.id}`)}
          onChange={(event) =>
            toggleSelectItem(
              `${row.id}`,
              isShiftClick(event),
              visibleRows.map((visibleRow) => `${visibleRow.id}`)
            )
          }
        />
      ),
    },
    {
      field: 'commtrac_external_id',
      headerName: 'Network ID',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.commtrac_external_id,
    },

    {
      field: 'date',
      headerName: 'Date',
      width: 160,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.date,
    },
    {
      field: 'pos_lat',
      headerName: 'Lat',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.pos_lat,
    },
    {
      field: 'pos_lon',
      headerName: 'Lon',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.pos_lon,
    },
    {
      field: 'beacon_data',
      headerName: 'Beacon Data',
      width: 200,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.beacon_data,
    },
  ];

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = openedItem?.selectedIds ?? [];

  const selectedRows = useMemo(() => {
    const theseItems = rows.filter((i) => selectedItems?.includes(`${i.id}`));

    theseItems.forEach((itm) => {
      itm.type =
        amsNodes.find(
          (node) => node.commtrac_external_id === itm.commtrac_external_id
        )?.type || 'undefined';
    });

    return theseItems;
  }, [rows, selectedItems]);

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<string>();

  const toggleSelectItem = (
    id: string,
    shiftKey: boolean,
    visibleIds: string[]
  ) => {
    if (openedItem && activeId) {
      const nextSelectedIds = getNextSelectedIds({
        id,
        visibleIds,
        selectedIds: selectedItems ?? [],
        shiftKey,
      });
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            selectedIds: nextSelectedIds,
            selectAll: nextSelectedIds.length === fetchedData?.items.length,
          },
        },
      });
    }
  };

  const selectAll = () => {
    if (openedItem && activeId) {
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            selectAll: true,
            selectedIds: rows?.map((i) => `${i.id}`) ?? [],
          },
        },
      });
    }
  };

  const unselectAll = () => {
    if (openedItem && activeId) {
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            selectAll: false,
            selectedIds: [],
          },
        },
      });
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  useEffect(() => {
    if (
      fetchedData &&
      openedItem?.selectedIds &&
      openedItem.selectedIds.length !== selectedRows.length &&
      activeId
    ) {
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            selectedIds: selectedRows.map((i) => `${i}`),
          },
        },
      });
    }
  }, [openedItem?.selectedIds, fetchedData]);

  const prevSelectedAll = usePrevious(selectedAll);

  useEffect(() => {
    if (prevSelectedAll && !selectedAll) {
      selectAll();
    }
  }, [rows]);

  const exportFields = columns
    .filter((col) => !col.doNotExport)
    .map((col) => ({
      field: col.field,
      label: col.headerName ?? col.field,
      hidden: shownFields?.indexOf(col.field) === -1,
    }));

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem && activeId) {
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            columnWidths: widths,
          },
        },
      });
    }
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      if (openedItem && activeId && tabData) {
        onUpdate?.({
          tabData: {
            ...tabData,
            [activeId]: {
              ...openedItem,
              shownFields: fields,
            },
          },
        });
      }
    },
    [onUpdate, activeId, tabData, openedItem]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem && activeId && tabData) {
        onUpdate?.({
          tabData: {
            ...tabData,
            [activeId]: {
              ...openedItem,
              columnOrder: order,
              shownFields: fields,
            },
          },
        });
      }
    },
    [onUpdate, activeId, tabData, openedItem]
  );

  const openTab = (id: string) => {
    if (!openedItems.includes(id)) {
      onUpdate?.({
        openedItems: [...openedItems, id],
        activeId: id,
        tabData: {
          ...tabData,
          [id]: getAMSLocationHistoryReportDataTab(id),
        },
      });
    } else {
      onUpdate?.({
        activeId: id,
      });
    }
  };

  const closeTab = (id: string) => {
    const newOpenedItems = openedItems.filter((i) => i !== id);
    const newTabData = {...tabData};
    delete newTabData[id];
    onUpdate?.({
      openedItems: newOpenedItems,
      activeId:
        newOpenedItems.length > 0
          ? newOpenedItems[newOpenedItems.length - 1]
          : undefined,
      tabData: newTabData,
    });
  };

  const changeTab = (_: any, id: string) => {
    onUpdate?.({
      activeId: id,
    });
  };

  return (
    <>
      <DashboardPanelTitleSlot>
        AMS Location History Report
      </DashboardPanelTitleSlot>

      <Box ref={printContainerRef} sx={PRINTABLE_CONTAINER_SX}>
        <ResizableColumns
          left={
            <Map
              sx={PRINTABLE_MAP_SX}
              panel="ams_location_tracking_report"
              selectedMapLayers={mapLayers}
              selectedLevel={mapLevel}
              amsLocationHistoryData={selectedRows}
              minWidth={250}
              availableMapLayers={[
                'street',
                'mine',
                'ams_location',
                'nodes',
                'alarms',
              ]}
              onSelectMapLayers={(v) => {
                onUpdate?.({
                  mapLayers: v,
                });
              }}
              onSelectLevel={(v) => {
                onUpdate?.({
                  mapLevel: v,
                });
              }}
            />
          }
        >
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            width="100%"
            overflow="hidden"
          >
            <Box
              sx={PRINT_HIDDEN_SX}
              display="flex"
              flexDirection="column"
              gap={3}
              py={1.5}
              bgcolor={(theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
              }
            >
              {isOpenAwayFromConnectView && (
                <Box pr={1.5}>
                  <PopupState variant="popover" popupId="demo-popup-menu">
                    {(popupState) => (
                      <>
                        <Button variant="outlined" {...bindTrigger(popupState)}>
                          Select an Object
                        </Button>
                        <Menu {...bindMenu(popupState)}>
                          {amsNodes?.map((it) => (
                            <MenuItem
                              key={`${it.emodule_id}-${it.id}`}
                              onClick={() => {
                                it?.emodule_id &&
                                  it?.id &&
                                  openTab(`${it.emodule_id}-${it.id}`);
                                popupState.close();
                              }}
                            >
                              {it.name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </PopupState>
                </Box>
              )}
              {openedItems.length ? (
                <Box pr={2}>
                  <Tabs
                    value={openedItem?.id}
                    variant="scrollable"
                    onChange={changeTab}
                  >
                    {openedItems.map((id, idx) => (
                      <Tab
                        key={id}
                        value={id}
                        label={
                          <TabLabel
                            name={tabNames?.[idx] ?? 'Undefined'}
                            onClose={() => closeTab(id)}
                          />
                        }
                      />
                    ))}
                  </Tabs>
                </Box>
              ) : (
                <Box minWidth={400} pr={1.5}>
                  <Alert severity="warning">No Assets Opened</Alert>
                </Box>
              )}

              <Box display="flex" flexDirection="column" pr={1.5}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box display="flex" gap={1}>
                    <Box minWidth={400}>
                      <DateRangeSelect
                        value={
                          openedItem
                            ? [
                                dayjs(openedItem.params?.date_start).toDate(),
                                dayjs(openedItem.params?.date_end).toDate(),
                              ]
                            : undefined
                        }
                        size="small"
                        disabled={!openedItem}
                        onChange={(v) => {
                          if (openedItem && activeId) {
                            onUpdate?.({
                              tabData: {
                                ...tabData,
                                [activeId]: {
                                  ...openedItem,
                                  selectAll: true,
                                  params: {
                                    ...openedItem.params,
                                    date_start: v?.[0]
                                      ? dayjs(v?.[0]).format('YYYY-MM-DD')
                                      : undefined,
                                    date_end: v?.[0]
                                      ? dayjs(v?.[1]).format('YYYY-MM-DD')
                                      : undefined,
                                  },
                                },
                              },
                            });
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  <Box display="flex" sx={{flex: 1, minWidth: 0}}>
                    <Box sx={{flex: 1, minWidth: 0, textAlign: 'right'}}>
                      <AdaptiveButtonGroup disabled={!openedItem}>
                        <Button size="small" data-priority={1}>
                          <AMSLocationHistoryExportButton
                            value={{
                              exportFields: exportFields,
                              params: params ?? undefined,
                              sensorId: sensorId,
                            }}
                          />
                        </Button>

                        <Button
                          size="small"
                          onClick={() => params && fetchData(params)}
                          data-priority={2}
                        >
                          <RefreshIcon />
                        </Button>

                        <AutoRefreshSelect
                          value={openedItem?.refreshInterval ?? null}
                          data-priority={3}
                          onChange={(v) => {
                            if (openedItem && activeId) {
                              onUpdate?.({
                                tabData: {
                                  ...tabData,
                                  [activeId]: {
                                    ...openedItem,
                                    refreshInterval: v,
                                  },
                                },
                              });
                            }
                          }}
                        />

                        <Button
                          size="small"
                          onClick={() => printReport()}
                          data-priority={4}
                        >
                          <PrintIcon />
                        </Button>
                      </AdaptiveButtonGroup>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Backdrop
              open={fetchedInProgress}
              sx={{position: 'absolute', zIndex: 1199}}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            {fetchedErrors.map((error, idx) => (
              <Alert
                key={idx}
                severity="error"
                onClose={() => params && fetchData(params)}
              >
                {error}
              </Alert>
            ))}

            <DataGrid
              ref={dataGridRef}
              rows={rows}
              columns={columns}
              loading={fetchedInProgress}
              shownFields={shownFields}
              defaultShownFields={DEFAULT_SHOWN_FIELDS}
              columnWidths={openedItem?.columnWidths}
              columnOrder={openedItem?.columnOrder}
              pagination
              paginationMode="server"
              size="small"
              sortBy={
                params?.order
                  ? {
                      field: params?.order,
                      dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                    }
                  : null
              }
              sortingMode="server"
              page={params?.page}
              pageSize={params?.limit}
              rowCount={fetchedData?.count}
              sxFooter={{
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
              }}
              onPageChange={(v) => {
                if (openedItem && activeId) {
                  onUpdate?.({
                    tabData: {
                      ...tabData,
                      [activeId]: {
                        ...openedItem,
                        selectAll: true,
                        params: {
                          ...openedItem.params,
                          page: v,
                        },
                      },
                    },
                  });
                }
              }}
              onPageSizeChange={(v) => {
                if (openedItem && activeId) {
                  onUpdate?.({
                    tabData: {
                      ...tabData,
                      [activeId]: {
                        ...openedItem,
                        selectAll: true,
                        params: {
                          ...openedItem.params,
                          page: 0,
                          limit: v,
                        },
                      },
                    },
                  });
                }
              }}
              onSort={(v) => {
                if (v && openedItem && activeId) {
                  onUpdate?.({
                    tabData: {
                      ...tabData,
                      [activeId]: {
                        ...openedItem,
                        params: {
                          ...openedItem.params,
                          order: v.field,
                          dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                        },
                      },
                    },
                  });
                }
              }}
              onShownFieldsChange={handleShownFieldsChange}
              onColumnWidthsChange={handleColumnWidthChange}
              onColumnOrderAndShownFieldsChange={
                handleColumnOrderAndShownFieldsChange
              }
            />
          </Box>
        </ResizableColumns>
      </Box>
    </>
  );
};
